import { fetchCompanyClients } from './orderService';
import {
  fetchCurrentDeviceId,
  fetchOutletPrinters,
  fetchSectionPrinterMappings,
  fetchTakeAwayPrinterMappings,
} from './printerService';
import type {
  BillPrintTicketClient,
  BillPrintTicketItem,
  OutletPrinter,
} from '../types/printer';
import type { SalesItemView, SalesReportOrderDetails } from '../types/report';

/**
 * Group order items into printable bill lines (merging duplicate
 * menu_item_id + unit_price rows, skipping cancelled items).
 * Shared by any flow that reprints/prints a bill or receipt from
 * SalesReportOrderDetails (report-order-details reprint, credit-bill payoff).
 */
export const buildBillItems = (
  items: SalesItemView[],
): BillPrintTicketItem[] => {
  const grouped = items
    .filter((item) => item.status !== 'CANCELLED')
    .reduce<Map<string, BillPrintTicketItem>>((accumulator, item) => {
      const key = `${item.menu_item_id}:${item.unit_price}`;
      const existing = accumulator.get(key);

      if (existing) {
        existing.quantity += item.quantity;
        existing.amount += item.total_amount;
        return accumulator;
      }

      accumulator.set(key, {
        name: item.item_name,
        quantity: item.quantity,
        amount: item.total_amount,
      });
      return accumulator;
    }, new Map());

  return Array.from(grouped.values()).sort((left, right) =>
    left.name.localeCompare(right.name),
  );
};

export const resolveBillClientDetails = async (
  details: SalesReportOrderDetails,
): Promise<BillPrintTicketClient | null> => {
  if (!details.order.client_id && !details.order.client_name) {
    return null;
  }

  if (!details.order.client_id) {
    return {
      name: details.order.client_name,
    };
  }

  const clientsResponse = await fetchCompanyClients();
  if (!clientsResponse.success || !clientsResponse.data) {
    return {
      name: details.order.client_name,
    };
  }

  const matchedClient = clientsResponse.data.clients.find(
    (client) => client.id === details.order.client_id,
  );

  if (!matchedClient) {
    return {
      name: details.order.client_name,
    };
  }

  return {
    name: matchedClient.full_name || details.order.client_name || null,
    address: matchedClient.address || null,
    tin_number: matchedClient.tin_number || null,
    email: matchedClient.email || null,
    contact: matchedClient.contact || null,
  };
};

export const resolveBillPrinter = async (
  details: SalesReportOrderDetails,
): Promise<OutletPrinter> => {
  const outletId = details.order.outlet_id;
  const printersResponse = await fetchOutletPrinters(outletId);
  if (!printersResponse.success || !printersResponse.data) {
    throw new Error(
      printersResponse.error || 'Failed to load outlet printers for this order',
    );
  }

  const outletPrinters = printersResponse.data;
  const isTakeAwayLikeOrder =
    details.order.mode !== 'DINE' || !details.order.section_id;

  if (isTakeAwayLikeOrder) {
    const deviceIdResponse = await fetchCurrentDeviceId();
    if (!deviceIdResponse.success || !deviceIdResponse.data) {
      throw new Error(
        deviceIdResponse.error ||
          'Current device ID is not available for takeaway printing',
      );
    }

    const mappingsResponse = await fetchTakeAwayPrinterMappings(
      outletId,
      deviceIdResponse.data,
    );
    if (!mappingsResponse.success || !mappingsResponse.data) {
      throw new Error(
        mappingsResponse.error ||
          'Failed to load takeaway printer mappings for this order',
      );
    }

    const mapping = mappingsResponse.data.find(
      (entry) =>
        entry.operation === 'BILL' &&
        !entry.department_id &&
        Boolean(entry.printer_id),
    );

    if (!mapping) {
      throw new Error(
        'No BILL printer is configured for takeaway orders on this device',
      );
    }

    const printer = outletPrinters.find(
      (entry) => entry.id === mapping.printer_id && entry.is_active,
    );

    if (!printer) {
      throw new Error('Configured BILL printer is missing or inactive');
    }

    return printer;
  }

  const mappingsResponse = await fetchSectionPrinterMappings(outletId);
  if (!mappingsResponse.success || !mappingsResponse.data) {
    throw new Error(
      mappingsResponse.error ||
        'Failed to load section printer mappings for this order',
    );
  }

  const mapping = mappingsResponse.data.find(
    (entry) =>
      entry.section_id === details.order.section_id &&
      entry.operation === 'BILL' &&
      !entry.department_id &&
      Boolean(entry.printer_id),
  );

  if (!mapping) {
    throw new Error('No BILL printer is configured for this section');
  }

  const printer = outletPrinters.find(
    (entry) => entry.id === mapping.printer_id && entry.is_active,
  );

  if (!printer) {
    throw new Error('Configured BILL printer is missing or inactive');
  }

  return printer;
};
